import { createContext, ReactNode, useContext, useMemo } from "react";

type DateValue = string | number | Date | null | undefined;
type NumberValue = string | number | null | undefined;
type TextValue = string;

interface FormatContextType {
  formatDateToDMY: (dateValue: DateValue) => string;
  formatDateOnly: (dateValue: DateValue) => string;
  formatNumber: (value: NumberValue) => string;
  formatCurrency: (value: NumberValue) => string;
  toSafeNumber: (value: NumberValue) => number;
  toCaptionCase: (value: TextValue) => string;
}

const FormatContext = createContext<FormatContextType | undefined>(undefined);

interface FormatProviderProps {
  children: ReactNode;
}

export const FormatProvider = ({ children }: FormatProviderProps) => {
  const toSafeNumber = (value: NumberValue): number => {
    if (value === null || value === undefined || value === "") return 0;

    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    const cleanedValue = String(value)
      .replace(/RM/gi, "")
      .replace(/MYR/gi, "")
      .replace(/,/g, "")
      .trim();

    const numericValue = Number(cleanedValue);

    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const formatNumber = (value: NumberValue): string => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toSafeNumber(value));
  };

  const formatCurrency = (value: NumberValue): string => {
    return new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toSafeNumber(value));
  };

  const formatDateToDMY = (dateValue: DateValue): string => {
    if (!dateValue) return "-";

    const raw = String(dateValue).trim();
    const malaysiaTimeZone = "Asia/Kuala_Lumpur";

    const formatMalaysiaDate = (date: Date): string => {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: malaysiaTimeZone,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).formatToParts(date);

      const getPart = (type: string) =>
        parts.find((part) => part.type === type)?.value || "";

      const day = getPart("day");
      const month = getPart("month");
      const year = getPart("year");
      const hour = getPart("hour");
      const minute = getPart("minute");
      const dayPeriod = getPart("dayPeriod").toUpperCase();

      return `${day}-${month}-${year} ${hour}:${minute} ${dayPeriod}`;
    };

    const formatParts = (
      year: string,
      month: string,
      day: string,
      hour: string = "00",
      minute: string = "00",
    ): string => {
      let hoursNum = Number(hour);
      const ampm = hoursNum >= 12 ? "PM" : "AM";
      hoursNum = hoursNum % 12 || 12;

      return `${day}-${month}-${year} ${String(hoursNum).padStart(
        2,
        "0",
      )}:${String(minute).padStart(2, "0")} ${ampm}`;
    };

    // Match: yyyy-mm-dd
    // Date only has no timezone issue, so keep the original date.
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return formatParts(year, month, day);
    }

    // Match: yyyy-mm-dd hh:mm:ss
    // Treat SQL datetime as UTC, then convert to Malaysia time.
    const sqlDateTimeMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
    );

    if (sqlDateTimeMatch) {
      const [, year, month, day, hour, minute, second = "00"] =
        sqlDateTimeMatch;

      const utcDate = new Date(
        `${year}-${month}-${day}T${hour}:${minute}:${second}Z`,
      );

      if (!isNaN(utcDate.getTime())) {
        return formatMalaysiaDate(utcDate);
      }
    }

    // Match: yyyy-mm-ddThh:mm:ss or ISO-like
    const isoDateTimeMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/,
    );

    if (isoDateTimeMatch) {
      const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);

      const isoDate = new Date(hasTimezone ? raw : `${raw}Z`);

      if (!isNaN(isoDate.getTime())) {
        return formatMalaysiaDate(isoDate);
      }
    }

    // Fallback for Date objects / timestamps / other parseable values
    const date = new Date(dateValue);

    if (!isNaN(date.getTime())) {
      return formatMalaysiaDate(date);
    }

    return raw;
  };

  const formatDateOnly = (dateValue: DateValue): string => {
    if (!dateValue) return "-";

    const raw = String(dateValue).trim();
    const malaysiaTimeZone = "Asia/Kuala_Lumpur";

    const formatMalaysiaDateOnly = (date: Date): string => {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: malaysiaTimeZone,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).formatToParts(date);

      const getPart = (type: string) =>
        parts.find((part) => part.type === type)?.value || "";

      const day = getPart("day");
      const month = getPart("month");
      const year = getPart("year");

      return `${day}-${month}-${year}`;
    };

    // Match: yyyy-mm-dd
    // Date only should stay exactly as DB date, no timezone conversion.
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return `${day}-${month}-${year}`;
    }

    // Match: yyyy-mm-dd hh:mm:ss
    // Treat SQL datetime as UTC, then convert to Malaysia date.
    const sqlDateTimeMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/,
    );

    if (sqlDateTimeMatch) {
      const [, year, month, day, hour, minute, second = "00"] =
        sqlDateTimeMatch;

      const utcDate = new Date(
        `${year}-${month}-${day}T${hour}:${minute}:${second}Z`,
      );

      if (!isNaN(utcDate.getTime())) {
        return formatMalaysiaDateOnly(utcDate);
      }
    }

    // Match: ISO datetime
    const isoDateTimeMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/,
    );

    if (isoDateTimeMatch) {
      const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
      const isoDate = new Date(hasTimezone ? raw : `${raw}Z`);

      if (!isNaN(isoDate.getTime())) {
        return formatMalaysiaDateOnly(isoDate);
      }
    }

    const date = new Date(dateValue);

    if (!isNaN(date.getTime())) {
      return formatMalaysiaDateOnly(date);
    }

    return raw;
  };

  const toCaptionCase = (value: string) => {
    if (!value) return "";

    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const value = useMemo(
    () => ({
      formatDateToDMY,
      formatDateOnly,
      formatNumber,
      formatCurrency,
      toSafeNumber,
      toCaptionCase,
    }),
    [],
  );

  return (
    <FormatContext.Provider value={value}>{children}</FormatContext.Provider>
  );
};

export const useFormat = (): FormatContextType => {
  const context = useContext(FormatContext);

  if (!context) {
    throw new Error("useFormat must be used inside FormatProvider");
  }

  return context;
};
